import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../utils/i18n';
import client from '../utils/sanityClient';

const HeroLanding = ({ title, subtitle, buttonText, buttonLink, cta = true }) => {
  const { language } = useLanguage();
  const [featuredProduct, setFeaturedProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch products from Sanity
  useEffect(() => {
    const loadFeaturedProduct = async () => {
      try {
        setIsLoading(true);
        console.log('Fetching products from Sanity for hero...');
        
        // Fetch products with images from Sanity
        const sanityProducts = await client.fetch(`
          *[_type == "product" && defined(images) && count(images) > 0] { 
            _id,
            name,
            "slug": slug.current,
            price,
            description,
            stock,
            "category": category->name,
            images[]{ 
              _key,
              alt,
              principal,
              asset->{
                _id,
                url 
              }
            }
          }
        `);
        
        console.log('Fetched products from Sanity:', sanityProducts);
        
        if (sanityProducts && sanityProducts.length > 0) {
          // Filter products that have stock and images
          const availableProducts = sanityProducts.filter(product => 
            product.stock > 0 && 
            product.images && 
            product.images.length > 0 &&
            product.images.some(img => img.asset?.url)
          );
          
          console.log('Available products with stock and images:', availableProducts);
          
          if (availableProducts.length > 0) {
            // Select a random product
            const randomIndex = Math.floor(Math.random() * availableProducts.length);
            const selectedProduct = availableProducts[randomIndex];
            
            // Map to the format expected by the component
            const mainImage = selectedProduct.images?.find(img => img.principal) || selectedProduct.images?.[0];
            const mappedProduct = {
              id: selectedProduct._id,
              name: selectedProduct.name,
              slug: selectedProduct.slug,
              image: mainImage?.asset?.url,
              price: selectedProduct.price || 0,
              material: selectedProduct.description || 'Material no especificado',
              dimensions: selectedProduct.description || '', // You might want to add a dimensions field to Sanity
              category: selectedProduct.category,
              stock: selectedProduct.stock || 0,
              _original: selectedProduct
            };
            
            console.log('Selected featured product:', mappedProduct);
            setFeaturedProduct(mappedProduct);
          }
        }
      } catch (error) {
        console.error('Failed to load products from Sanity:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadFeaturedProduct();
  }, []);

  // Format price in Mexican Peso
  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0,
    }).format(price);
  };

  return (
    <div className="relative min-h-[80vh] flex items-center justify-center overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(139,69,19,0.1),transparent_50%)]"></div>
        <div className="absolute inset-0 bg-[linear-gradient(45deg,rgba(139,69,19,0.05)_1px,transparent_1px),linear-gradient(-45deg,rgba(139,69,19,0.05)_1px,transparent_1px)] bg-[size:60px_60px]"></div>
      </div>
      
      {/* Main Content Container */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-12 items-center">
        
        {/* Left Side - Main Text */}
        <motion.div 
          className="text-left space-y-8"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h1 className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold text-akasha-brown mb-6 font-serif leading-tight">
              {title}
            </h1>
          </motion.div>
          
          {subtitle && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <p className="text-xl sm:text-2xl lg:text-3xl text-akasha-black/80 font-light leading-relaxed max-w-2xl">
                {subtitle}
              </p>
            </motion.div>
          )}

          {cta && buttonText && buttonLink && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="pt-4"
            >
              <Link 
                to={buttonLink} 
                className="group inline-flex items-center gap-4 bg-akasha-brown text-white px-10 py-5 text-xl rounded-2xl hover:bg-akasha-brown/90 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 font-medium"
              >
                {buttonText}
                <svg className="w-6 h-6 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
            </motion.div>
          )}
        </motion.div>

        {/* Right Side - Featured Product */}
        <motion.div 
          className="relative hidden lg:block"
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
        >
          {/* Product showcase card */}
          <div className="relative">
            <div className="bg-white/20 backdrop-blur-xl border border-white/30 shadow-2xl rounded-[3rem] p-8 transform rotate-2 hover:rotate-0 transition-transform duration-500 group">
              
              {/* Product image container */}
              <div className="relative h-80 rounded-[2rem] overflow-hidden bg-gradient-to-br from-white/40 to-akasha-brown/5">
                {isLoading ? (
                  // Loading state
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-akasha-brown/10 to-akasha-brown/5">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-akasha-brown/20 rounded-2xl mx-auto mb-4 animate-pulse"></div>
                      <p className="text-akasha-brown/60 text-sm">Cargando producto...</p>
                    </div>
                  </div>
                ) : featuredProduct ? (
                  <>
                    <img 
                      src={featuredProduct.image}
                      alt={featuredProduct.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      onError={(e) => {
                        // If Sanity image fails, show placeholder
                        console.log('Sanity image failed to load:', featuredProduct.name, featuredProduct.image);
                        e.target.parentElement.innerHTML = `
                          <div class="w-full h-full flex items-center justify-center bg-gradient-to-br from-akasha-brown/10 to-akasha-brown/5">
                            <div class="text-center">
                              <div class="w-16 h-16 bg-akasha-brown/20 rounded-2xl mx-auto mb-4"></div>
                              <p class="text-akasha-brown/60 text-sm">Imagen no disponible</p>
                            </div>
                          </div>
                        `;
                      }}
                    />
                    
                    {/* Product overlay info */}
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="bg-white/30 backdrop-blur-sm rounded-2xl p-3 transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                        <h3 className="font-semibold text-akasha-brown text-base mb-1 line-clamp-1">
                          {featuredProduct.name}
                        </h3>
                        <div className="flex items-center justify-between">
                          <span className="text-akasha-brown font-bold text-lg">
                            {formatPrice(featuredProduct.price)}
                          </span>
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${featuredProduct.stock > 5 ? 'bg-green-500' : featuredProduct.stock > 0 ? 'bg-yellow-500' : 'bg-red-500'}`}></div>
                            <span className="text-[10px] text-akasha-black/60">
                              {featuredProduct.stock > 5 ? 'Disponible' : featuredProduct.stock > 0 ? 'Pocas piezas' : 'Agotado'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Category badge */}
                    {featuredProduct.category && (
                      <div className="absolute top-4 right-4">
                        <div className="bg-akasha-brown text-white px-3 py-1.5 rounded-full text-xs font-medium shadow-lg">
                          {featuredProduct.category}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  // No products available
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-akasha-brown/10 to-akasha-brown/5">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-akasha-brown/20 rounded-2xl mx-auto mb-4"></div>
                      <p className="text-akasha-brown/60 text-sm">No hay productos disponibles</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Floating elements */}
            <motion.div 
              className="absolute -top-4 -right-4 w-16 h-16 bg-akasha-brown/10 rounded-2xl backdrop-blur-sm"
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.div 
              className="absolute -bottom-6 -left-6 w-12 h-12 bg-akasha-brown/20 rounded-xl backdrop-blur-sm"
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1 }}
            />
            
            {/* Decorative dots */}
            <motion.div 
              className="absolute top-1/2 -left-8 w-3 h-3 bg-akasha-brown/30 rounded-full"
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.div 
              className="absolute bottom-1/4 -right-8 w-2 h-2 bg-akasha-brown/20 rounded-full"
              animate={{ scale: [1, 1.3, 1] }}
              transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
            />
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default HeroLanding; 